


"use client";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiSend } from "react-icons/fi";
import "./ChatInput.css";

const ChatInput = ({ onSendMessage, onUploadFile, onTyping }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");

  // ✅ multiple attachments
  const [attachedFiles, setAttachedFiles] = useState([]); // Array<File>

  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  // ✅ object URLs cache (for previews)
  const objectUrlsRef = useRef(new Map()); // key -> objectUrl

  const handleTyping = useCallback(() => {
    onTyping?.(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      onTyping?.(false);
    }, 1500);
  }, [onTyping]);

  // ✅ build stable keys per file
  const fileKey = (f) => `${f.name}_${f.size}_${f.lastModified}`;

  // ✅ create / reuse object URL for image preview
  const getPreviewUrl = useCallback((file) => {
    const key = fileKey(file);
    const cache = objectUrlsRef.current;
    if (cache.has(key)) return cache.get(key);
    const url = URL.createObjectURL(file);
    cache.set(key, url);
    return url;
  }, []);

  // ✅ cleanup all object URLs on unmount
  useEffect(() => {
    return () => {
      try {
        for (const url of objectUrlsRef.current.values()) URL.revokeObjectURL(url);
      } catch {}
      objectUrlsRef.current.clear();
    };
  }, []);

  const removeOneFile = useCallback((keyToRemove) => {
    setAttachedFiles((prev) => {
      const next = prev.filter((f) => fileKey(f) !== keyToRemove);

      // revoke URL for removed file if any
      const cache = objectUrlsRef.current;
      const url = cache.get(keyToRemove);
      if (url) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
        cache.delete(keyToRemove);
      }

      return next;
    });
  }, []);

  const clearAllFiles = useCallback(() => {
    // revoke all
    const cache = objectUrlsRef.current;
    for (const url of cache.values()) {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    }
    cache.clear();
    setAttachedFiles([]);
  }, []);

  const handleSubmit = useCallback(() => {
    if (isSubmittingRef.current) return;
    if (!message.trim() && attachedFiles.length === 0) return;

    isSubmittingRef.current = true;

    // ✅ upload all files (one by one -> compatible with your current backend)
    if (attachedFiles.length) {
      for (const f of attachedFiles) {
        onUploadFile?.(f);
      }
      clearAllFiles();
    }

    // ✅ send message
    if (message.trim()) {
      onSendMessage?.(message.trim());
      setMessage("");
    }

    onTyping?.(false);
    isSubmittingRef.current = false;
  }, [message, attachedFiles, onUploadFile, onSendMessage, onTyping, clearAllFiles]);

  const handleChange = (e) => {
    setMessage(e.target.value);
    handleTyping();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // ✅ pick multiple files
  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setAttachedFiles((prev) => {
      const map = new Map(prev.map((f) => [fileKey(f), f]));
      for (const f of files) map.set(fileKey(f), f); // dedupe by key
      return Array.from(map.values());
    });

    e.target.value = "";
  };

  const openFilePicker = () => fileInputRef.current?.click();

  useEffect(() => {
    const onGlobalEnter = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        if ((message && message.trim()) || attachedFiles.length) {
          e.preventDefault();
          handleSubmit();
        }
      }
    };
    window.addEventListener("keydown", onGlobalEnter);
    return () => window.removeEventListener("keydown", onGlobalEnter);
  }, [message, attachedFiles.length, handleSubmit]);

  const hasSomethingToSend = Boolean(message.trim()) || attachedFiles.length > 0;

  return (
    <div className="chat-input-container">
      {/* ✅ MULTI PREVIEW */}
      {attachedFiles.length > 0 && (
        <div className="chat-attachment-preview">
          {attachedFiles.map((file) => {
            const key = fileKey(file);
            const isImg = /^image\//i.test(file.type);

            return (
              <div key={key} className="chat-attachment-item">
                {isImg ? (
                  <img
                    src={getPreviewUrl(file)}
                    alt={file.name}
                    className="chat-attachment-image"
                  />
                ) : (
                  <div className="chat-attachment-file">{file.name}</div>
                )}

                {/* ✅ remove icon (X) */}
                <button
                  type="button"
                  className="chat-attachment-remove"
                  onClick={() => removeOneFile(key)}
                  aria-label={t("messaging.chatInput.removeAttachment")}
                  title={t("messaging.chatInput.removeAttachment")}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="chat-input-wrapper">
        <div className="message-input-container">
          <textarea
            ref={inputRef}
            className="message-input"
            placeholder={
              attachedFiles.length
                ? t("messaging.chatInput.placeholderWithAttachment")
                : t("messaging.chatInput.placeholder")
            }
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            rows={1}
          />
        </div>

        <button type="button" className="input-attach-button" onClick={openFilePicker}>
          <FiPlus className="input-attach-icon" />
        </button>

        {hasSomethingToSend && (
          <button
            type="button"
            className="chat-input-button send-btn"
            onClick={handleSubmit}
          >
            <FiSend className="chat-input-icon" />
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="file-upload-input"
        onChange={handleFilePick}
        multiple
        accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx"
      />
    </div>
  );
};

export default ChatInput;
